import DOMPurify from 'dompurify'
import React from 'react'

import { ICON_TAGS } from './iconTagAllowlist'
import { parseIconMarkup } from './parseIconMarkup'
import type { RuntimeIconProps } from './types'

/**
 * An allowlist, and the same one the native gate walks — not `USE_PROFILES` plus a denylist.
 *
 * The profile is far broader than the renderers: `text`, `linearGradient`, `pattern`, `symbol`,
 * `marker`, `animateTransform` and `animateMotion` all survive it, and native drops every one of
 * them. That is the same web/native split `iconRenderTags.parity.spec.ts` was written about, still
 * open at the render gate. A denylist cannot close it, because nothing enumerates what the profile
 * admits; naming the tags we draw does.
 *
 * `USE_PROFILES` silently overrides an `ALLOWED_TAGS` passed alongside it, so the profile has to go
 * rather than be narrowed. Verified byte-identical output over all 232 committed tenant glyphs.
 */
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [...ICON_TAGS],
    FORBID_ATTR: ['style', 'href', 'xlink:href', 'onload', 'onerror', 'onclick'],
    ALLOW_DATA_ATTR: false,
}

const cache = new Map<string, React.ComponentType<RuntimeIconProps> | null>()

/**
 * Turns config-supplied markup into something `<Icon as={…}>` accepts. Sanitised and parsed once
 * per glyph rather than per render, since the result is cached by markup.
 *
 * Sanitising again here is not belt-and-braces: the markup reaches the DOM through
 * dangerouslySetInnerHTML, so this is the last gate before it becomes live nodes.
 *
 * Root paint from the markup is spread last so it wins over the forwarded props. Normalisation
 * neutralises the root stroke for fill-only glyphs, and the forwarded stroke would re-apply it.
 *
 * Dimensions fall back to the markup's own, so a glyph rendered by a consumer that forwards no
 * size keeps the size it declares - matching the compiled component this replaces. Without that
 * fallback an inline `<svg>` carrying only a viewBox stretches to fill its container.
 */
export const createRuntimeIcon = (markup: string): React.ComponentType<RuntimeIconProps> | null => {
    const cached = cache.get(markup)
    if (cached !== undefined) {
        return cached
    }
    const parsed = parseIconMarkup(DOMPurify.sanitize(markup, SANITIZE_CONFIG))
    if (!parsed) {
        cache.set(markup, null)
        return null
    }
    const html = { __html: parsed.inner }

    const RuntimeIcon = React.forwardRef<SVGSVGElement, RuntimeIconProps>(function RuntimeIcon(
        { size, height, width, color, fill, stroke, className, style, testID, ...rest },
        ref,
    ) {
        return (
            <svg
                ref={ref}
                viewBox={parsed.viewBox}
                width={width ?? size ?? parsed.intrinsic.width}
                height={height ?? size ?? parsed.intrinsic.height}
                color={color}
                fill={fill}
                stroke={stroke}
                className={className}
                style={style as React.CSSProperties}
                data-testid={testID}
                {...rest}
                {...parsed.rootPaint}
                dangerouslySetInnerHTML={html}
            />
        )
    })
    RuntimeIcon.displayName = 'RuntimeIcon'

    cache.set(markup, RuntimeIcon)
    return RuntimeIcon
}
